import {
  getRefreshToken,
  shouldRefreshToken,
  isTokenValid,
  setToken,
  setRefreshToken,
  clearAuth,
  isLoginSessionActive,
  getRawToken
} from "./secureStorage";
import { store } from "../redux/store/store";
import { updateToken, logout } from "../redux/slices/auth";
import { showToastError } from "../helper/toast";

const REFRESH_TOKEN_ENDPOINT = "/auth/refresh";

let isRefreshing = false;
let refreshPromise = null;

export const refreshAccessToken = async () => {
  if (isRefreshing) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    if (!isLoginSessionActive()) {
      showToastError("Your session has expired. Please login again.");
    }
    return null;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";

      const response = await fetch(`${baseUrl}${REFRESH_TOKEN_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();

      if (data.status === "success" && data.data?.token) {
        const newToken = data.data.token;
        const newRefreshToken = data.data.refreshToken;
        setToken(newToken, 57600);
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
        }
        store.dispatch(updateToken({
          token: newToken,
          refreshToken: newRefreshToken
        }));
        return newToken;
      }
      throw new Error("Invalid refresh response");
    } catch (error) {
      console.error("Token refresh error:", error);
      if (isLoginSessionActive()) {
        showToastError("Unable to refresh session. Please login again.");
      } else {
        showToastError("Your session has expired. Please login again.");
      }
      clearAuth();
      store.dispatch(logout());
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const ensureValidToken = async () => {
  if (!isTokenValid()) {
    return await refreshAccessToken();
  }
  if (shouldRefreshToken()) {
    refreshAccessToken();
  }
  const { getToken } = await import("./secureStorage");
  return getToken();
};

// Removed setupAutoRefresh - Web Worker handles background refresh reliably
// setInterval in the main thread gets throttled in background tabs

export const setupVisibilityRefresh = () => {
  const handleVisibilityChange = async () => {
    if (document.visibilityState === "visible") {
      const hasToken = getRawToken();
      if (!hasToken) {
        return;
      }
      if (!isLoginSessionActive()) {
        showToastError("Your session has expired. Please login again.");
        clearAuth();
        store.dispatch(logout());
        return;
      }

      if (!isTokenValid() || shouldRefreshToken()) {
        await refreshAccessToken();
        updateWorkerExpiry();
      }
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
};

let tokenWorker = null;

const updateWorkerExpiry = () => {
  const newExpiry = localStorage.getItem('auth_token_expiry');
  if (newExpiry && tokenWorker) {
    tokenWorker.postMessage({
      type: 'UPDATE_EXPIRY',
      payload: { tokenExpiry: Number.parseInt(newExpiry) }
    });
  }
};

export const setupWorkerRefresh = () => {
  if (typeof Worker === 'undefined') {
    return null;
  }

  try {
    tokenWorker = new Worker(
      new URL('../workers/tokenRefreshWorker.js', import.meta.url)
    );

    tokenWorker.onmessage = async (e) => {
      const { type } = e.data;
      switch (type) {
        case 'REFRESH_NEEDED': {
          const newToken = await refreshAccessToken();
          if (newToken) {
            updateWorkerExpiry();
          }
          break;
        }

        case 'TOKEN_EXPIRED':
          await refreshAccessToken();
          updateWorkerExpiry();
          break;

        case 'STARTED':
        case 'STOPPED':
          break;

        default:
          break;
      }
    };

    tokenWorker.onerror = () => { };

    const tokenExpiry = localStorage.getItem('auth_token_expiry');
    if (tokenExpiry) {
      tokenWorker.postMessage({
        type: 'START',
        payload: {
          tokenExpiry: Number.parseInt(tokenExpiry),
          refreshBuffer: 60 * 60 * 1000
        }
      });
    }

    return tokenWorker;
  } catch (error) {
    console.error("Worker setup error:", error);
    return null;
  }
};

export const stopWorkerRefresh = () => {
  if (tokenWorker) {
    tokenWorker.postMessage({ type: 'STOP' });
    tokenWorker.terminate();
    tokenWorker = null;
  }
};

export const restartWorkerRefresh = () => {
  const tokenExpiry = localStorage.getItem('auth_token_expiry');
  if (tokenExpiry && tokenWorker) {
    tokenWorker.postMessage({
      type: 'START',
      payload: {
        tokenExpiry: Number.parseInt(tokenExpiry),
        refreshBuffer: 60 * 60 * 1000
      }
    });
  }
};

export default {
  refreshAccessToken,
  ensureValidToken,
  setupVisibilityRefresh,
  setupWorkerRefresh,
  stopWorkerRefresh,
  restartWorkerRefresh,
};
